import { memo, useMemo, type FC } from "react";
import { useListWebhooks, type PlainWebhook } from "@/queries/webhook-queries";
import { Combobox } from "@/components/flexkit/combobox";
import { WEBHOOK_PROVIDER_CONFIG } from "@/components/webhooks/webhook-config";
import { WebhookProvider } from "gitpod-next-api/gitpod/v1/webhook_pb";
import { webhooksUrl } from "@/utils/automations";
import { Link } from "@/components/flexkit/Link";
import { IconSettings } from "@/assets/icons/geist/IconSettings";

type WebhookSelectorProps = {
    selectedWebhookId?: string;
    disabled?: boolean;
    onSelectionChange: (webhookId: string | undefined) => void;
};

export const WebhookSelector: FC<WebhookSelectorProps> = memo(
    ({ selectedWebhookId, disabled = false, onSelectionChange }) => {
        const { data: webhooks, isLoading } = useListWebhooks();

        const selectedWebhook = useMemo(
            () => webhooks?.find((w) => w.id === selectedWebhookId),
            [webhooks, selectedWebhookId],
        );

        const handleValueChange = (value: string) => {
            onSelectionChange(value || undefined);
        };

        let comboboxContent: React.ReactNode;
        if (isLoading) {
            comboboxContent = <Combobox.Loading>Loading webhooks…</Combobox.Loading>;
        } else if (!webhooks || webhooks.length === 0) {
            comboboxContent = (
                <>
                    <Combobox.Empty>No webhooks configured.</Combobox.Empty>
                    <Combobox.Footer>
                        <ManageWebhooksLink />
                    </Combobox.Footer>
                </>
            );
        } else {
            comboboxContent = (
                <>
                    <Combobox.List
                        items={webhooks}
                        searchKeys={["metadata.name", "metadata.description", "spec.scope"]}
                        noMatchesComponent={<Combobox.Empty>No matching webhooks.</Combobox.Empty>}
                    >
                        {(webhook) => <WebhookSelectItem key={webhook.id} webhook={webhook} />}
                    </Combobox.List>
                    <Combobox.Footer>
                        <ManageWebhooksLink />
                    </Combobox.Footer>
                </>
            );
        }

        return (
            <Combobox
                value={selectedWebhookId ?? ""}
                onValueChange={handleValueChange}
                loading={isLoading}
                disabled={disabled}
            >
                <Combobox.Value>
                    {selectedWebhook ? (
                        <WebhookValueDisplay webhook={selectedWebhook} />
                    ) : (
                        <Combobox.ValueLabel className="text-content-muted">Select a webhook</Combobox.ValueLabel>
                    )}
                </Combobox.Value>
                <Combobox.Popover>{comboboxContent}</Combobox.Popover>
            </Combobox>
        );
    },
);
WebhookSelector.displayName = "WebhookSelector";

const WebhookValueDisplay: FC<{ webhook: PlainWebhook }> = memo(({ webhook }) => {
    const provider = webhook.spec?.provider ?? WebhookProvider.UNSPECIFIED;
    const ProviderIcon = WEBHOOK_PROVIDER_CONFIG[provider].icon;
    const name = webhook.metadata?.name || "Unnamed webhook";

    return (
        <Combobox.ValueLabel className="flex items-center gap-2">
            <ProviderIcon size="sm" className="flex-none text-content-secondary" />
            <span className="truncate">{name}</span>
        </Combobox.ValueLabel>
    );
});
WebhookValueDisplay.displayName = "WebhookValueDisplay";

const WebhookSelectItem: FC<{ webhook: PlainWebhook }> = memo(({ webhook }) => {
    const provider = webhook.spec?.provider ?? WebhookProvider.UNSPECIFIED;
    const ProviderIcon = WEBHOOK_PROVIDER_CONFIG[provider].icon;
    const name = webhook.metadata?.name || "Unnamed webhook";
    const description = webhook.metadata?.description;

    return (
        <Combobox.ListItem value={webhook.id}>
            <Combobox.ListItemLeadingIcon>
                <ProviderIcon size="sm" className="text-content-secondary" />
            </Combobox.ListItemLeadingIcon>
            <Combobox.ListItemTitle>{name}</Combobox.ListItemTitle>
            {description && <Combobox.ListItemDescription>{description}</Combobox.ListItemDescription>}
        </Combobox.ListItem>
    );
});
WebhookSelectItem.displayName = "WebhookSelectItem";

const ManageWebhooksLink: FC = memo(() => {
    return (
        <Link to={webhooksUrl()} className="flex w-full items-center gap-2 text-sm text-content-brand">
            <IconSettings size="sm" className="flex-none" />
            <span>Manage webhooks</span>
        </Link>
    );
});
ManageWebhooksLink.displayName = "ManageWebhooksLink";
